import { createSlice } from '@reduxjs/toolkit';
import { getShareOfMetrics, getSocialMetricsComparison } from '@/services/mediaService';
import {
  getShareOfVoice,
  getShareOfVoiceCalendar,
  getShareOfVoiceMonthly,
} from '@/services/shareOfVoiceService';
import { getCollaborationCreators } from '@/services/collaborationCreatorsService';
import { logoutUser } from '@/store/slices/authSlice';
import {
  ShareOfMetricsData,
  SocialMetricsComparisonResponse,
  ShareOfMetricsParams,
  SocialMetricsComparisonParams,
} from '@/types/media.types';
import type {
  ShareOfVoiceCalendarParams,
  ShareOfVoiceCalendarResponse,
  ShareOfVoiceMonthlyParams,
  ShareOfVoiceMonthlyResponse,
  ShareOfVoiceParams,
  ShareOfVoiceResponse,
} from '@/types/shareOfVoice.types';
import type {
  CollaborationCreatorsParams,
  CollaborationCreatorsResponse,
} from '@/types/collaborationCreators.types';
import {
  DataStateWithParams,
  createDataAsyncThunk,
  addDataAsyncCasesWithParams,
} from '@/store/utils/asyncThunkHelpers';

interface MediaState {
  shareOfMetrics: DataStateWithParams<ShareOfMetricsData, ShareOfMetricsParams>;
  socialMetricsComparison: DataStateWithParams<
    SocialMetricsComparisonResponse,
    SocialMetricsComparisonParams
  >;
  shareOfVoice: DataStateWithParams<ShareOfVoiceResponse, ShareOfVoiceParams>;
  /** Second chart: one calendar month, brands on the X axis. Its own entry so its
   *  loading/error state and `lastParams` cache key never collide with the trend. */
  shareOfVoiceMonthly: DataStateWithParams<ShareOfVoiceMonthlyResponse, ShareOfVoiceMonthlyParams>;
  /** Same month as the monthly chart, seen as a calendar: one row per day, every
   *  metric summed. Its own slot so switching views never evicts the other's data. */
  shareOfVoiceCalendar: DataStateWithParams<
    ShareOfVoiceCalendarResponse,
    ShareOfVoiceCalendarParams
  >;
  collaborationCreators: DataStateWithParams<
    CollaborationCreatorsResponse,
    CollaborationCreatorsParams
  >;
}

const initialState: MediaState = {
  shareOfMetrics: { data: null, status: 'idle', error: null },
  socialMetricsComparison: { data: null, status: 'idle', error: null },
  shareOfVoice: { data: null, status: 'idle', error: null },
  shareOfVoiceMonthly: { data: null, status: 'idle', error: null },
  shareOfVoiceCalendar: { data: null, status: 'idle', error: null },
  collaborationCreators: { data: null, status: 'idle', error: null },
};

export const fetchShareOfMetrics = createDataAsyncThunk<ShareOfMetricsData, ShareOfMetricsParams>(
  'media/fetchShareOfMetrics',
  getShareOfMetrics
);

export const fetchSocialMetricsComparison = createDataAsyncThunk<
  SocialMetricsComparisonResponse,
  SocialMetricsComparisonParams
>('media/fetchSocialMetricsComparison', getSocialMetricsComparison);

export const fetchShareOfVoice = createDataAsyncThunk<ShareOfVoiceResponse, ShareOfVoiceParams>(
  'media/fetchShareOfVoice',
  getShareOfVoice
);

export const fetchShareOfVoiceMonthly = createDataAsyncThunk<
  ShareOfVoiceMonthlyResponse,
  ShareOfVoiceMonthlyParams
>('media/fetchShareOfVoiceMonthly', getShareOfVoiceMonthly);

export const fetchShareOfVoiceCalendar = createDataAsyncThunk<
  ShareOfVoiceCalendarResponse,
  ShareOfVoiceCalendarParams
>('media/fetchShareOfVoiceCalendar', getShareOfVoiceCalendar);

export const fetchCollaborationCreators = createDataAsyncThunk<
  CollaborationCreatorsResponse,
  CollaborationCreatorsParams
>('media/fetchCollaborationCreators', getCollaborationCreators);

const emptyShareOfMetrics = (): MediaState['shareOfMetrics'] => ({
  data: null,
  status: 'idle',
  error: null,
  lastParams: undefined,
});

const emptySocialMetrics = (): MediaState['socialMetricsComparison'] => ({
  data: null,
  status: 'idle',
  error: null,
  lastParams: undefined,
});

const emptyShareOfVoice = (): MediaState['shareOfVoice'] => ({
  data: null,
  status: 'idle',
  error: null,
  lastParams: undefined,
});

const emptyShareOfVoiceCalendar = (): MediaState['shareOfVoiceCalendar'] => ({
  data: null,
  status: 'idle',
  error: null,
  lastParams: undefined,
});

const emptyShareOfVoiceMonthly = (): MediaState['shareOfVoiceMonthly'] => ({
  data: null,
  status: 'idle',
  error: null,
  lastParams: undefined,
});

const emptyCollaborationCreators = (): MediaState['collaborationCreators'] => ({
  data: null,
  status: 'idle',
  error: null,
  lastParams: undefined,
});

const mediaSlice = createSlice({
  name: 'media',
  initialState,
  reducers: {
    clearShareOfMetrics: state => {
      state.shareOfMetrics = emptyShareOfMetrics();
    },
    clearSocialMetricsComparison: state => {
      state.socialMetricsComparison = emptySocialMetrics();
    },
    clearShareOfVoice: state => {
      state.shareOfVoice = emptyShareOfVoice();
    },
    clearShareOfVoiceMonthly: state => {
      state.shareOfVoiceMonthly = emptyShareOfVoiceMonthly();
    },
    clearShareOfVoiceCalendar: state => {
      state.shareOfVoiceCalendar = emptyShareOfVoiceCalendar();
    },
    clearCollaborationCreators: state => {
      state.collaborationCreators = emptyCollaborationCreators();
    },
  },
  extraReducers: builder => {
    // Use enhanced helper with parameter tracking
    addDataAsyncCasesWithParams(builder, fetchShareOfMetrics, state => state.shareOfMetrics);
    addDataAsyncCasesWithParams(
      builder,
      fetchSocialMetricsComparison,
      state => state.socialMetricsComparison
    );
    addDataAsyncCasesWithParams(builder, fetchShareOfVoice, state => state.shareOfVoice);
    addDataAsyncCasesWithParams(
      builder,
      fetchShareOfVoiceMonthly,
      state => state.shareOfVoiceMonthly
    );
    addDataAsyncCasesWithParams(
      builder,
      fetchShareOfVoiceCalendar,
      state => state.shareOfVoiceCalendar
    );
    addDataAsyncCasesWithParams(
      builder,
      fetchCollaborationCreators,
      state => state.collaborationCreators
    );

    // Reset state on logout
    builder.addCase(logoutUser.fulfilled, () => {
      return initialState;
    });
  },
});

export const {
  clearShareOfMetrics,
  clearSocialMetricsComparison,
  clearShareOfVoice,
  clearShareOfVoiceMonthly,
  clearShareOfVoiceCalendar,
  clearCollaborationCreators,
} = mediaSlice.actions;
export default mediaSlice.reducer;
